<template>
  <BaseScreen v-bind="screen">
    <BaseMenu
      v-if="isAdmin"
      :items="menuItems"
    />
    <BaseForm
      v-else
      ref="form"
      v-bind="form"
      novalidate
      class="screen__form"
      @submit="checkPassword"
    />
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import screenMixin from '@/mixins/screen';
import terminalMixin from '@/mixins/terminal';
import BaseMenu from '@/components/BaseMenu.vue';
import BaseForm from '@/components/BaseForm.vue';
import FieldPassword from '@/components/FieldPassword.vue';

import CONSTANTS from '@/helpers/constants';

const {
  SCREEN_READY,
} = CONSTANTS;

export default {
  name: 'AdminScreen',
  mixins: [screenMixin, terminalMixin],
  components: {
    BaseMenu,
    BaseForm,
  },
  data() {
    return {
      errorCode: 0,
      errorMessage: '',
    };
  },
  computed: {
    ...mapState([
      'isAdmin',
    ]),
    title() {
      return this.$t('titles.admin');
    },
    error() {
      return {
        title: String(this.errorCode),
        text: this.errorMessage,
        actions: this.isAdmin ? [
          {
            text: this.$t('actions.backToMenu'),
            action: this.hideError,
          },
        ] : [
          {
            text: this.$t('actions.tryAgain'),
            action: this.retryAuth,
          },
        ],
      };
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          isSuccess: true,
        },
        title: this.$t('titles.success'),
        text: this.$t('texts.operationComplete'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
          {
            text: this.$t('actions.backToMenu'),
            action: this.hideThanks,
          },
        ],
      };
    },
    form() {
      return {
        fields: [
          {
            id: 'password',
            component: FieldPassword,
          },
        ],
        submitButtonText: this.$t('actions.confirm'),
      };
    },
    menuItems() {
      return [
        {
          icon: {
            name: 'refund',
            isPied: true,
          },
          text: this.$t('actions.refundTicket'),
          action: () => this.$router.push(this.$router.options.paths.refund),
          isSquare: true,
        },
        {
          icon: {
            name: 'open',
          },
          text: this.$t('actions.openSession'),
          action: this.openSession,
          isMiddle: true,
        },
        {
          icon: {
            name: 'lock',
          },
          text: this.$t('actions.closeSession'),
          action: this.closeSession,
          isMiddle: true,
        },
      ];
    },
  },
  beforeRouteLeave(to, from, next) {
    // Удаляем права администратора при переходе вне refund
    if (to.path !== this.$router.options.paths.refund) {
      this.removeAdminPermissions();
    }
    next();
  },
  mounted() {
    this.initActions();
  },
  methods: {
    ...mapMutations([
      'addAdminPermissions',
      'removeAdminPermissions',
    ]),
    checkPassword({ password }) {
      if (password !== process.env.VUE_APP_ADMIN_PASSWORD) {
        this.onTerminalError(403, this.$t('fields.wrongPassword'));
        return;
      }
      this.addAdminPermissions();
    },
    openSession() {
      if (this.isPending) return;
      this.openTerminalSession();
      this.showPending();
    },
    closeSession() {
      if (this.isPending) return;
      this.closeTerminalSession();
      this.showPending();
    },
    onTerminalSessionOpen() {
      this.showThanks();
    },
    onTerminalSessionClose() {
      this.showThanks();
    },
    onTerminalError(code, message) {
      this.errorCode = code;
      this.errorMessage = message;
      this.showError();
    },
    onTerminalPaperOut() {
      this.onTerminalError(500, this.$t('titles.paperOut'));
    },
    retryAuth() {
      this.hideError();
      this.$nextTick(this.setFieldFocus);
    },
    setFieldFocus() {
      return this.$refs.form?.setFocus();
    },
    initActions() {
      const rootActions = [
        [SCREEN_READY, this.setFieldFocus],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>
